import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Check, Loader2, ShieldCheck, Navigation } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useLang } from "../../lib/i18n";

export default function Contact({ settings }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", inquiry_type: "Reservation", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [human, setHuman] = useState(false);
  const [checking, setChecking] = useState(false);
  const [answer, setAnswer] = useState("");

  const startCaptcha = async () => {
    if (human || checking) return;
    setChecking(true);
    try {
      const { data } = await api.get("/captcha");
      setCaptcha(data);
      setHuman(true);
    } catch {
      toast.error(t("captcha.failed"));
    } finally {
      setChecking(false);
    }
  };

  const resetCaptcha = () => {
    setCaptcha(null);
    setHuman(false);
    setAnswer("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", {
        ...form,
        captcha_token: captcha?.token || "",
        captcha_answer: answer,
      });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", company: "", inquiry_type: "Reservation", message: "", website: "" });
      resetCaptcha();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("captcha")) {
        toast.error(t("captcha.failed"));
        resetCaptcha();
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#E5DED4] focus:border-[#9A7B66] rounded-xl px-4 py-3 text-sm font-sans text-[#2C2825] outline-none transition-colors";

  const types = ["type.1", "type.2", "type.3", "type.4", "type.5"];

  // Derive the villa coordinates from the configured map embed so both stay in sync
  const coordsMatch = String(settings?.map_embed_url || "").match(/q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  const directionsUrl = coordsMatch ? `https://www.google.com/maps/dir/?api=1&destination=${coordsMatch[1]},${coordsMatch[2]}` : "";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-[#1E1A17] text-[#F4EFEA]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-xs uppercase tracking-[0.3em] font-sans font-semibold text-[#9A7B66] mb-5"
        >
          {t("contact.eyebrow")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-serif font-normal tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl mb-16"
        >
          {t("contact.title")}
        </motion.h2>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <p className="text-base text-[#A0988E] font-sans font-light leading-relaxed max-w-md">
              {t("contact.intro")}
            </p>
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-[#9A7B66] mt-0.5 shrink-0" />
                <span className="text-sm font-sans text-[#D3C8B9]">{settings?.contact_address}</span>
              </div>
              <div className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-[#9A7B66] shrink-0" />
                <a href={`mailto:${settings?.contact_email}`} className="text-sm font-sans text-[#D3C8B9] hover:text-white transition-colors">
                  {settings?.contact_email}
                </a>
              </div>
              <div className="flex items-center gap-4">
                <Phone className="w-5 h-5 text-[#9A7B66] shrink-0" />
                <a href={`tel:${settings?.contact_phone}`} className="text-sm font-sans text-[#D3C8B9] hover:text-white transition-colors">
                  {settings?.contact_phone}
                </a>
              </div>
            </div>
            <div className="rounded-md overflow-hidden h-64 border border-white/10">
              <iframe
                data-testid="contact-map"
                title="Nusadira Lembongan location"
                src={settings?.map_embed_url}
                className="w-full h-full grayscale-[35%] contrast-[1.05]"
                loading="lazy"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {directionsUrl && (
                <a
                  data-testid="contact-directions-button"
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 px-6 py-3 rounded-sm border border-[#9A7B66] text-[#C9A66B] hover:bg-[#9A7B66] hover:text-white text-[11px] font-sans font-semibold uppercase tracking-[0.16em] transition-colors"
                >
                  <Navigation className="w-4 h-4" />
                  {t("contact.directions")}
                </a>
              )}
              {settings?.google_maps_url && (
                <a
                  data-testid="contact-google-maps-link"
                  href={settings.google_maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 px-6 py-3 rounded-sm border border-white/20 text-[#D3C8B9] hover:border-[#9A7B66] hover:text-white text-[11px] font-sans font-semibold uppercase tracking-[0.16em] transition-colors"
                >
                  <MapPin className="w-4 h-4" />
                  {t("contact.viewOnMaps")}
                </a>
              )}
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            onSubmit={submit}
            className="bg-[#26211D] rounded-md p-7 sm:p-9 space-y-5 border border-white/5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <input
                data-testid="contact-name-input"
                required
                placeholder={t("contact.name")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="contact-email-input"
                required
                type="email"
                placeholder={t("contact.emailPh")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <input
                data-testid="contact-phone-input"
                placeholder={t("contact.phone")}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputCls}
              />
              <select
                data-testid="contact-type-select"
                value={form.inquiry_type}
                onChange={(e) => setForm({ ...form, inquiry_type: e.target.value })}
                className={inputCls}
              >
                {types.map((k, i) => (
                  <option key={k} value={["Reservation", "Experiences", "Events & Weddings", "Press", "General"][i]}>
                    {t(k)}
                  </option>
                ))}
              </select>
            </div>
            <input
              data-testid="contact-company-input"
              placeholder={t("contact.company")}
              value={form.company || ""}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className={inputCls}
            />
            <textarea
              data-testid="contact-message-input"
              required
              rows={5}
              placeholder={t("contact.message")}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={`${inputCls} resize-none`}
            />

            <input
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="absolute -left-[9999px] opacity-0 h-0 w-0"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <div data-testid="captcha-widget" className="bg-white rounded-xl border border-[#E5DED4] p-4">
              <button
                type="button"
                data-testid="captcha-checkbox"
                onClick={startCaptcha}
                className="flex items-center gap-3 w-full text-left"
              >
                <span
                  className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                    human ? "bg-[#9A7B66] border-[#9A7B66]" : "border-[#D3C8B9] bg-white"
                  }`}
                >
                  {checking ? (
                    <Loader2 className="w-4 h-4 text-[#9A7B66] animate-spin" />
                  ) : human ? (
                    <Check className="w-4 h-4 text-white" />
                  ) : null}
                </span>
                <span className="text-sm font-sans text-[#6E675F]">{t("captcha.label")}</span>
                <ShieldCheck className="w-4 h-4 text-[#9A7B66] ml-auto" />
              </button>
              {human && captcha && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="flex items-center gap-3 mt-3 pl-9 overflow-hidden"
                >
                  <span className="text-sm font-sans text-[#6E675F]">
                    {t("captcha.verify")}: <strong className="text-[#9A7B66]">{captcha.question} =</strong>
                  </span>
                  <input
                    data-testid="captcha-answer-input"
                    type="text"
                    inputMode="numeric"
                    required
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder={t("captcha.ph")}
                    className="w-24 bg-white border border-[#E5DED4] focus:border-[#9A7B66] rounded-lg px-3 py-1.5 text-sm text-[#2C2825] outline-none transition-colors"
                  />
                </motion.div>
              )}
            </div>

            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending || !human || !answer.trim()}
              className="w-full sm:w-auto px-10 py-3.5 rounded-full bg-[#9A7B66] hover:bg-[#826552] disabled:opacity-50 disabled:cursor-not-allowed text-[#FBF9F5] text-[13px] font-sans font-semibold tracking-[0.12em] uppercase transition-all duration-300"
            >
              {sending ? t("contact.sending") : t("contact.send")}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
